
const idOf = enemy => String(enemy?.enemyId || enemy?._id || '');
const canonical = value => JSON.stringify(value, (_, entry) => entry && typeof entry === 'object' && !Array.isArray(entry)
  ? Object.keys(entry).sort().reduce((sorted, key) => ({ ...sorted, [key]: entry[key] }), {}) : entry);
const attackFields = action => Object.fromEntries(['name', 'desc', 'attack_bonus', 'damage', 'damage_dice', 'damage_type']
  .filter(key => action[key] !== undefined).map(key => [key, action[key]]));
const validAttack = action => action && action.name?.toLowerCase() !== 'multiattack' && !action.multiattack && Number.isFinite(action.attack_bonus)
  && (action.damage_dice || Array.isArray(action.damage) && action.damage.some(part => part.damage_dice));

export const isMultiattackAction = action => /^multiattack(?:$|\s*\()/i.test(action?.name || '');

/** Bind only explicit reviewed references. Prose is displayed, never parsed. */
export function getMultiattackPlan(enemy, multi, bundledPlans) {
  if (multi?.name?.toLowerCase() !== 'multiattack') return { supported: false, reason: 'Not a Multiattack action.' };
  const actions = enemy?.actions || [];
  if (!Array.isArray(actions) || actions.some(action => !action || typeof action !== 'object' || Array.isArray(action))) return { supported:false, reason:'The action list is invalid.' };
  if (multi.multiattack !== undefined) {
    if (!Array.isArray(multi.multiattack) || !multi.multiattack.length || multi.multiattack.length > 100)
      return { supported: false, reason: 'The structured attack list is empty or invalid.' };
    const ids = actions.map(action => action.action_id).filter(Boolean);
    if (ids.length !== new Set(ids).size) return { supported: false, reason: 'Duplicate action references need correction.' };
    let total = 0;
    const pools = [];
    for (const [index, entry] of multi.multiattack.entries()) {
      if (!entry || typeof entry.action_id !== 'string' || !entry.action_id.trim()) return { supported:false, reason:'An action reference is missing.' };
      const action = actions.find(candidate => candidate.action_id === entry.action_id);
      if (Object.keys(entry).some(key => !['action_id', 'count'].includes(key)) || !validAttack(action)
        || !Number.isInteger(entry.count) || entry.count < 1 || (total += entry.count) > 100)
        return { supported: false, reason: 'A structured reference, count, or ability is unsupported.' };
      pools.push({ id: String(index), count: entry.count, options: [{ id: entry.action_id, kind: 'attack', action: action.name, resolvedAction: action }] });
    }
    return { supported: true, version: 1, description: multi.desc || '', manualOptions: [], variants: [{ id: 'custom', pools }] };
  }
  const plan = enemy?.source !== 'custom' && bundledPlans[enemy.index];
  if (!plan || plan.description !== multi.desc || plan.actions.some(expected => {
    const matches = actions.filter(action => action.name === expected.name);
    return matches.length !== 1 || !validAttack(matches[0]) || canonical(attackFields(matches[0])) !== canonical(attackFields(expected));
  })) return { supported: false, reason: 'This description or campaign snapshot has no reviewed executable plan. Resolve it manually.' };
  return { ...plan, supported: true, variants: plan.variants.map(variant => ({ ...variant,
    pools: variant.pools.map(pool => ({ ...pool, options: pool.options.map(option => ({ ...option,
      ...(option.kind === 'attack' ? { resolvedAction: actions.find(action => action.name === option.action) } : {}) })) })) })) };
}

export function createMultiattackSequence(enemy, plan, turnSequence, id) {
  if (!idOf(enemy) || !plan.supported || !plan.variants.length) throw new Error('A reviewed Multiattack plan is required.');
  return { id, sourceCombatantId: idOf(enemy), turnSequence, plan, variantId: plan.variants.length === 1 ? plan.variants[0].id : null,
    history: [], pending: null, ended: false };
}
export function chooseMultiattackVariant(sequence, variantId) {
  if (sequence.pending || sequence.history.length || !sequence.plan.variants.some(variant => variant.id === variantId)) throw new Error('The routine cannot be changed after an attack starts.');
  return { ...sequence, variantId };
}
export function multiattackChoices(sequence) {
  if (!sequence || sequence.ended || sequence.pending) return [];
  const variant = sequence.plan.variants.find(entry => entry.id === sequence.variantId);
  return (variant?.pools || []).flatMap(pool => {
    const remaining = pool.count - sequence.history.filter(step => step.poolId === pool.id).length;
    return remaining <= 0 ? [] : pool.options.filter(option => {
      const used = sequence.history.filter(step => option.limitGroup ? step.option.limitGroup === option.limitGroup : step.poolId === pool.id && step.option.id === option.id).length;
      return !option.limit || used < option.limit;
    }).map(option => ({ poolId: pool.id, remaining, option }));
  });
}
export function selectMultiattackStep(sequence, poolId, optionId) {
  const choice = multiattackChoices(sequence).find(entry => entry.poolId === poolId && entry.option.id === optionId);
  if (!choice) throw new Error('Finish the current step or choose an available ability.');
  return { ...sequence, pending: { id: `${sequence.id}:step:${sequence.history.length + 1}`, poolId, option: choice.option, rolls: {}, status: 'targeting' } };
}
export function bindMultiattackTarget(sequence, targetId, target) {
  if (!sequence?.pending || sequence.ended || targetId === sequence.sourceCombatantId) throw new Error('No valid pending attack.');
  if (sequence.pending.targetId && sequence.pending.targetId !== targetId) throw new Error('Retry the current attack against its original target.');
  return { ...sequence, pending: { ...sequence.pending, targetId, target: sequence.pending.target || target, status: 'resolving' } };
}
export function completeMultiattackStep(sequence, stepId, outcome) {
  if (!sequence?.pending || sequence.pending.id !== stepId || sequence.ended) throw new Error('This Multiattack step is no longer pending.');
  return { ...sequence, history: [...sequence.history, { ...sequence.pending, outcome }], pending: null };
}
export function cancelMultiattackSelection(sequence) {
  if (!sequence?.pending || sequence.pending.targetId || sequence.pending.started) throw new Error('An attack that has started must be resolved or the sequence ended.');
  return { ...sequence, pending: null };
}

